import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Plus, 
  Send,
  CheckCircle,
  Clock,
  AlertCircle,
  Loader2,
  BarChart3,
  ArrowUp,
  ArrowDown,
  Activity,
  WifiOff,
  PlusCircle
} from 'lucide-react';
import { supabase } from '../lib/supabase';

interface Campaign {
  id: string;
  name: string;
  message_template: string;
  status: 'draft' | 'scheduled' | 'running' | 'completed' | 'cancelled';
  scheduled_at: string | null;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
  instance_id: string;
}

interface CampaignStats {
  messages_sent: number;
  messages_delivered: number;
  messages_read: number;
}

interface Instance {
  id: string;
  name: string;
}

export default function Campaigns() {
  const { instanceId: preselectedInstanceId } = useParams();
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [stats, setStats] = useState<Record<string, CampaignStats>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showNewModal, setShowNewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);
  const [instances, setInstances] = useState<Instance[]>([]);
  const [formData, setFormData] = useState({
    name: '',
    message_template: '',
    scheduled_at: '',
    instance_id: preselectedInstanceId || ''
  });

  useEffect(() => {
    fetchInstances();
    // Only fetch campaigns if we're not on the "new" route
    if (preselectedInstanceId && preselectedInstanceId !== 'new') {
      fetchCampaigns(preselectedInstanceId);
    } else if (!preselectedInstanceId) {
      fetchAllCampaigns();
    } else {
      // If we're on the "new" route, just set loading to false
      setLoading(false);
    }
  }, [preselectedInstanceId]);

  async function fetchInstances() {
    try {
      const { data, error: instancesError } = await supabase
        .from('instances')
        .select('id, name')
        .eq('is_active', true);

      if (instancesError) throw instancesError;
      setInstances(data || []);
    } catch (err: any) {
      console.error('Error fetching instances:', err);
      setError('Error al cargar las instancias');
    }
  }

  async function fetchAllCampaigns() {
    try {
      setLoading(true);
      setError('');

      const { data: campaignsData, error: campaignsError } = await supabase
        .from('campaigns')
        .select('*')
        .order('created_at', { ascending: false });

      if (campaignsError) throw campaignsError;

      setCampaigns(campaignsData || []);
      await fetchStatsForCampaigns(campaignsData || []);
    } catch (err: any) {
      console.error('Error fetching all campaigns:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function fetchCampaigns(instanceId: string) {
    try {
      setLoading(true);
      setError('');

      const { data: campaignsData, error: campaignsError } = await supabase
        .from('campaigns')
        .select('*')
        .eq('instance_id', instanceId)
        .order('created_at', { ascending: false });

      if (campaignsError) throw campaignsError;

      setCampaigns(campaignsData || []);
      await fetchStatsForCampaigns(campaignsData || []);
    } catch (err: any) {
      console.error('Error fetching campaigns:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function fetchStatsForCampaigns(campaigns: Campaign[]) {
    const statsData: Record<string, CampaignStats> = {};
    for (const campaign of campaigns) {
      const { data: statData, error: statError } = await supabase
        .from('campaign_stats')
        .select('*')
        .eq('campaign_id', campaign.id)
        .maybeSingle();

      if (!statError) {
        statsData[campaign.id] = statData || {
          messages_sent: 0,
          messages_delivered: 0,
          messages_read: 0
        };
      }
    }
    setStats(statsData);
  }

  function getStatusBadgeColor(status: string) {
    switch (status) {
      case 'draft':
        return 'bg-gray-100 text-gray-800';
      case 'scheduled':
        return 'bg-blue-100 text-blue-800';
      case 'running':
        return 'bg-green-100 text-green-800';
      case 'completed':
        return 'bg-purple-100 text-purple-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  }

  function getInstanceName(instanceId: string) {
    const instance = instances.find(i => i.id === instanceId);
    return instance ? instance.name : 'Instancia no encontrada';
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <button
              onClick={() => navigate('/')}
              className="flex items-center text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
              Volver a Canales
            </button>
          </div>
          <button
            onClick={() => navigate('/campaigns/new')}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            <PlusCircle className="h-5 w-5 mr-2" />
            Nueva Campaña
          </button>
        </div>

        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center">
              <Send className="h-5 w-5 text-gray-500 mr-2" />
              <h2 className="text-lg font-medium text-gray-900">
                Campañas
              </h2>
            </div>
          </div>

          {error && (
            <div className="px-6 py-4">
              <div className="bg-red-50 border-l-4 border-red-400 p-4">
                <div className="flex">
                  <AlertCircle className="h-5 w-5 text-red-400" />
                  <div className="ml-3">
                    <p className="text-sm text-red-700">{error}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Nombre
                  </th>
                  {!preselectedInstanceId && (
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Instancia
                    </th>
                  )}
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Estado
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Programada para
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Estadísticas
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-4 text-center text-sm text-gray-500">
                      <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2" />
                      Cargando campañas...
                    </td>
                  </tr>
                ) : campaigns.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-4 text-center text-sm text-gray-500">
                      No hay campañas para mostrar
                    </td>
                  </tr>
                ) : (
                  campaigns.map((campaign) => (
                    <tr key={campaign.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {campaign.name}
                        </div>
                      </td>
                      {!preselectedInstanceId && (
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {getInstanceName(campaign.instance_id)}
                          </div>
                        </td>
                      )}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeColor(campaign.status)}`}>
                          {campaign.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {campaign.scheduled_at ? new Date(campaign.scheduled_at).toLocaleString() : 'No programada'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {stats[campaign.id] ? (
                            <div className="flex items-center space-x-4">
                              <div>
                                <span className="font-medium">{stats[campaign.id].messages_sent}</span>
                                <span className="text-gray-500 ml-1">enviados</span>
                              </div>
                              <div>
                                <span className="font-medium">{stats[campaign.id].messages_delivered}</span>
                                <span className="text-gray-500 ml-1">entregados</span>
                              </div>
                              <div>
                                <span className="font-medium">{stats[campaign.id].messages_read}</span>
                                <span className="text-gray-500 ml-1">leídos</span>
                              </div>
                            </div>
                          ) : (
                            <span className="text-gray-500">Sin estadísticas</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-2">
                          {/* Add action buttons here */}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}